import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { CLUSTERS } from '../../learn/_clusters';

/**
 * CollectionsStrip (homepage)
 *
 * Surfaces the top Learn collections (supplier-aligned content hubs) as a
 * visual tile grid so homepage visitors discover them without reaching
 * /learn. Pulls straight from the shared CLUSTERS config; each tile links
 * to its hub (/learn/<key>). A footer link opens the full collections
 * index.
 */

// Hand-picked order of the most visual, high-intent collections to feature
// on the homepage. All of these carry a lifestyle heroImage.
const FEATURED_KEYS = [
  'furnishing-your-home',
  'pergolas-and-outdoor-living',
  'bedroom-and-sleep',
  'kitchen-and-cookware',
  'styling-your-new-home',
  'tableware-and-glassware',
];

const FEATURED = FEATURED_KEYS
  .map((key) => (CLUSTERS[key] ? { key, ...CLUSTERS[key] } : null))
  .filter(Boolean);

const CollectionsStrip = () => {
  if (!FEATURED.length) return null;

  // Chips render the hero image at ~32px. Request a tiny version from Unsplash
  // (w=96, q=60) instead of the full 1100px hero so we don't download ~150KB
  // per chip for a thumbnail.
  const toThumb = (url) => {
    if (typeof url !== 'string' || !url.includes('images.unsplash.com')) return url;
    return url
      .replace(/([?&])w=\d+/i, '$1w=96')
      .replace(/([?&])q=\d+/i, '$1q=60')
      + (/([?&])w=\d+/i.test(url) ? '' : (url.includes('?') ? '&w=96' : '?w=96'));
  };

  return (
    <section className="py-9 sm:py-12 bg-white" data-testid="home-collections-strip">
      <div className="max-w-[1120px] mx-auto px-5 sm:px-6">
        <div className="flex flex-col items-center text-center gap-2 mb-5 sm:flex-row sm:items-end sm:justify-between sm:text-left">
          <div>
            <h2 className="text-[18px] sm:text-[22px] font-bold text-[#1d1d1f] tracking-tight leading-tight">
              Furnish, finish and kit out your home
            </h2>
            <p className="text-[13px] text-[#86868b] mt-1">
              Independent buying guides, by room and project.
            </p>
          </div>
          <Link
            to="/learn"
            className="shrink-0 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1d8efe] hover:text-[#0c6fd6] transition-colors"
            data-testid="home-collections-view-all"
          >
            View all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="flex flex-wrap gap-2 sm:gap-2.5" data-testid="home-collections-grid">
          {FEATURED.map((c) => (
            <Link
              key={c.key}
              to={`/learn/${c.key}`}
              className="group inline-flex items-center gap-2.5 pl-1.5 pr-3.5 py-1.5 rounded-full bg-white border border-gray-200 hover:border-[#1d8efe] hover:shadow-sm transition-all"
              data-testid={`home-collection-card-${c.key}`}
            >
              <img
                src={toThumb(c.heroImage)}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                width="32"
                height="32"
                className="w-8 h-8 rounded-full object-cover shrink-0"
              />
              <span className="text-[13px] font-semibold text-[#1d1d1f] group-hover:text-[#1d8efe] transition-colors whitespace-nowrap">
                {c.collectionName || c.h1}
              </span>
              <span className="text-[12px] text-[#a1a1a6] whitespace-nowrap">{(c.guides || []).length} guides</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CollectionsStrip;
